import { ROUTES } from '@cloud-ui/constants';
import { logout } from '@cloud-ui/contexts/AuthContext';
import {
  clearSessionAuthStorage,
  requestSessionReview,
  trackAuthSession,
} from '@cloud-ui/utils/authSessionStorage';
import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';
import invariant from '@promptfoo/util/invariant';
import { RATING_CONFLICT_CODE } from '@shared/dto/results';

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public text?: string,
    public json?: any,
    public isHtmlResponse = false,
  ) {
    super(message);
  }
}

function readableMessage(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  // Older validation responses encode Zod's issue list inside the error string.
  try {
    const issues = JSON.parse(value);
    if (
      Array.isArray(issues) &&
      issues.length > 0 &&
      issues.every(
        (issue) =>
          issue &&
          typeof issue.message === 'string' &&
          Array.isArray(issue.path) &&
          issue.path.every((part: unknown) => typeof part === 'string' || typeof part === 'number'),
      )
    ) {
      return issues
        .map((issue) => `${issue.path.length ? `${issue.path.join('.')}: ` : ''}${issue.message}`)
        .join('; ');
    }
  } catch {}

  return value;
}

/** Select a displayable message without assuming error response fields are strings. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.isHtmlResponse) {
      return error.message;
    }
    return (
      readableMessage(error.json?.message) ??
      readableMessage(error.json?.error) ??
      readableMessage(error.text) ??
      readableMessage(error.message) ??
      fallback
    );
  }
  return error instanceof Error ? (readableMessage(error.message) ?? fallback) : fallback;
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;

type CallApiModifiers = {
  skip401Redirect?: boolean;
  suppressErrorLogStatuses?: number[];
};

function buildApiUrl(path: string, version: 'v1' | 'v2'): string {
  // Trim whitespace
  let cleanedPath = path.trim();

  // Path must be a non-empty string
  invariant(!!cleanedPath, 'Path is required');

  // Trim leading slash
  if (cleanedPath.startsWith('/')) {
    cleanedPath = cleanedPath.slice(1);
  }

  // Construct the API URL
  return `${API_BASE_URL}/api/${version}/${cleanedPath}`;
}

export function getApiUrl(path: string): string {
  return buildApiUrl(path, 'v1');
}

function isAbortError(error: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) {
    return true;
  }

  return error instanceof DOMException && error.name === 'AbortError';
}

function containsGraderExamples(body: unknown): boolean {
  if (typeof body !== 'string' || (!body.includes('graderExamples') && !body.includes('\\u'))) {
    return false;
  }

  let found = false;
  try {
    JSON.parse(body, (key, value) => {
      if (key === 'graderExamples') {
        found = true;
      }
      return value;
    });
  } catch {}
  return found;
}

function isModernRatingPath(path: string): boolean {
  const segments = path.split('?')[0]?.split('/').filter(Boolean) ?? [];
  return (
    segments.length === 5 &&
    segments[0] === 'results' &&
    segments[2] === 'results' &&
    segments[4] === 'rating'
  );
}

function isRatingPath(path: string): boolean {
  const segments = path.split('?')[0]?.split('/').filter(Boolean) ?? [];
  return (
    isModernRatingPath(path) ||
    (segments.length === 2 && segments[0] === 'eval' && segments[1] !== 'configs')
  );
}

async function createHttpError(
  path: string,
  response: Response,
  requestMayEchoGraderExamples: boolean,
  suppressErrorLogStatuses?: number[],
): Promise<ApiError> {
  const errorBody = await response.text();
  // A free-form server message can repeat submitted examples, so omit the whole error body.
  const omitErrorBody = requestMayEchoGraderExamples || containsGraderExamples(errorBody);
  if (!suppressErrorLogStatuses?.includes(response.status)) {
    console.error(
      `HTTP error ${response.status}: ${response.statusText}\nResponse body:`,
      omitErrorBody ? '[omitted]' : errorBody,
    );
  }

  let jsonErrorBody;
  if (!omitErrorBody) {
    try {
      jsonErrorBody = JSON.parse(errorBody);
    } catch {}
  } else if (response.status === 409 && isModernRatingPath(path)) {
    try {
      if (JSON.parse(errorBody)?.code === RATING_CONFLICT_CODE) {
        jsonErrorBody = { code: RATING_CONFLICT_CODE };
      }
    } catch {}
  }

  const errorDetail = omitErrorBody
    ? response.statusText || `HTTP ${response.status}`
    : errorBody || response.statusText || `HTTP ${response.status}`;
  // Gateways can return HTML even with missing or incorrect response headers.
  // Prefer parsed JSON over the header, and retain allowed bodies for diagnostics.
  const isHtmlResponse =
    jsonErrorBody === undefined &&
    (response.headers.get('content-type')?.toLowerCase().includes('text/html') ||
      /^\s*<(?:!doctype\s+html|html(?:\s|>))/i.test(errorBody));
  return new ApiError(
    isHtmlResponse
      ? `Request failed (HTTP ${response.status}). Please try again or contact support if it continues.`
      : (readableMessage(jsonErrorBody?.message) ??
          readableMessage(jsonErrorBody?.error) ??
          `Error calling ${path}: ${errorDetail}`),
    response.status,
    omitErrorBody ? undefined : errorBody,
    jsonErrorBody,
    isHtmlResponse,
  );
}

/** Diagnose JSON endpoints without consuming or logging their response bodies. */
function warnForUnexpectedContentType(response: Response): void {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return;
  }
  const contentType = response.headers.get('content-type');
  const mediaType = contentType?.split(';')[0].trim().toLowerCase();
  if (mediaType !== 'application/json' && !mediaType?.endsWith('+json')) {
    console.warn(`Expected a JSON response but received content type: ${contentType}`);
  }
}

/** An unavailable read is distinct from a known absent value when deciding cleanup ownership. */
function readAutomaticLogoutState() {
  return tryBrowserStorage(() => ({
    pendingInvite: localStorage.getItem('invite_code'),
    capturedInvite: sessionStorage.getItem('captured_invite_code'),
    usedCodes: localStorage.getItem('used_codes'),
  }));
}

/** An old request must not end an invitation or login flow that started while it was in flight. */
function trackAutomaticLogout() {
  const session = trackAuthSession();
  const state = readAutomaticLogoutState();
  return {
    status: (): 'current' | 'stale' | 'unknown' => {
      if (!session.isCurrent()) {
        return 'stale';
      }
      const current = readAutomaticLogoutState();
      if (!state || !current) {
        return 'unknown';
      }
      return current.pendingInvite === state.pendingInvite &&
        current.capturedInvite === state.capturedInvite &&
        current.usedCodes === state.usedCodes
        ? 'current'
        : 'stale';
    },
    dispose: session.dispose,
  };
}

async function callApiInternal(
  url: string,
  path: string,
  options: RequestInit = {},
  modifiers: CallApiModifiers = {},
): Promise<Response> {
  const headers = new Headers(options.headers);
  const body = options.body;
  const requestMayEchoGraderExamples = isRatingPath(path) && containsGraderExamples(body);

  // Avoid forcing a cross-origin preflight for bodyless requests.
  if (typeof body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const automaticLogout = modifiers.skip401Redirect ? undefined : trackAutomaticLogout();
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for session-based auth
    });

    if (response.ok) {
      return response;
    } else if (response.status === 401) {
      if (modifiers.skip401Redirect) {
        return response;
      }
      const sessionStatus = automaticLogout?.status();
      if (sessionStatus === 'current') {
        // Cleanup, the logout request and its broadcast all belong to the request's session.
        // Skipping only storage cleanup would still invalidate a newer OAuth flow in a peer tab.
        clearSessionAuthStorage();
        logout();
        window.location.href = `${ROUTES.login}?reason=unauthorized&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      } else if (sessionStatus === 'unknown') {
        // Unreadable storage cannot establish cleanup ownership. Let the user revalidate
        // explicitly without unloading an invitation or login that may have started meanwhile.
        requestSessionReview();
      }
      // Current and stale requests both reject; an unauthorized response is never valid data.
      throw new ApiError('Unauthorized', 401, '', {});
    } else {
      throw await createHttpError(
        path,
        response,
        requestMayEchoGraderExamples,
        modifiers.suppressErrorLogStatuses,
      );
    }
  } catch (error) {
    if (isAbortError(error, options.signal)) {
      throw error;
    }

    const safeError =
      requestMayEchoGraderExamples && !(error instanceof ApiError)
        ? new ApiError(`Error calling ${path}: Unable to complete request`)
        : error;
    // Don't log 401s to Sentry (via console.error) - they're already handled
    // Either by returning the response (skip401Redirect) or by redirecting to login
    const is401 = error instanceof ApiError && error.status === 401;
    const shouldSuppressErrorLog =
      error instanceof ApiError &&
      error.status !== undefined &&
      modifiers.suppressErrorLogStatuses?.includes(error.status);

    if (is401) {
      // Use console.debug for auth failures (not captured by Sentry)
      console.debug('401 Unauthorized:', { url, method: options.method || 'GET' });
    } else if (!shouldSuppressErrorLog) {
      // Log unexpected errors to console (and Sentry)
      console.error('API call failed:', safeError);
      console.error('Request details:', {
        url,
        method: options.method || 'GET',
        headers: Object.fromEntries(headers.entries()),
      });
    }
    throw safeError;
  } finally {
    automaticLogout?.dispose();
  }
}

/**
 * Call the v1 API
 */
export async function callApi(
  path: string,
  options: RequestInit = {},
  modifiers: CallApiModifiers = {},
): Promise<Response> {
  const url = buildApiUrl(path, 'v1');
  return callApiInternal(url, path, options, modifiers);
}

/** Read a JSON endpoint. Callers remain responsible for any DTO validation. */
export async function callApiJson<T = unknown>(
  path: string,
  options: RequestInit = {},
  modifiers: CallApiModifiers = {},
): Promise<T> {
  const response = await callApi(path, options, modifiers);
  const requestMayEchoGraderExamples = isRatingPath(path) && containsGraderExamples(options.body);
  try {
    // Raw callers can inspect a 401 without redirecting; JSON callers must not treat it as data.
    if (response.status === 401) {
      throw await createHttpError(path, response, requestMayEchoGraderExamples, [401]);
    }
    warnForUnexpectedContentType(response);
    return await response.json();
  } catch (error) {
    if (
      requestMayEchoGraderExamples &&
      !(error instanceof ApiError) &&
      !isAbortError(error, options.signal)
    ) {
      throw new ApiError(
        `Error calling ${path}: Unable to read response`,
        response.ok ? undefined : response.status,
      );
    }
    throw error;
  }
}
